/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  DeviceConfigurationService,
  type TServicesParams
} from "@tivocorp/data-domain-lib";

import { Log } from "../logging/Log";

export class Locale {
  private static TAG = "Locale";

  private static languageMap: Record<string, string> = {};

  static readonly setLocale = async (
    locale: string,
    msoName?: string,
    platform?: string
  ) => {
    const localeArr = locale.split("_");
    const language = localeArr[0].toLowerCase();
    const region = localeArr[1].toLowerCase();
    let languageFile: Record<string, string> = {};
    let regionLanguageFile: Record<string, string> = {};
    let platformLanguageFile: Record<string, string> = {};
    let msoLanguageFile: Record<string, string> = {};

    try {
      languageFile = await import(
        `../assets/locales/${language}_any_any_unmanaged_smarttv.json`
      );
    } catch (err: unknown) {
      const error = err as Error;
      Log.e(Locale.TAG, error.message);
      Log.i(Locale.TAG, "Using default locale file");
      const defaultLocale = "en";
      languageFile = await import(
        `../assets/locales/${defaultLocale}_any_any_unmanaged_smarttv.json`
      );
    }

    try {
      regionLanguageFile = await import(
        `../assets/locales/${language}_${region}_any_unmanaged_smarttv.json`
      );
    } catch (err: unknown) {
      const error = err as Error;
      Log.e(Locale.TAG, error.message);
    }

    try {
      msoLanguageFile = await import(
        `../assets/locales/${language}_${region}_${msoName}_unmanaged_smarttv.json`
      );
    } catch (err: unknown) {
      const error = err as Error;
      Log.e(Locale.TAG, error.message);
    }

    try {
      const platformLowerCase =
        platform !== null && platform !== undefined
          ? platform.toLowerCase()
          : "";
      platformLanguageFile = await import(
        `../assets/locales/${language}_any_any_${platformLowerCase}_unmanaged_smarttv.json`
      );
    } catch (err: unknown) {
      const error = err as Error;
      Log.e(Locale.TAG, error.message);
    }

    Object.assign(Locale.languageMap, languageFile);
    Object.assign(Locale.languageMap, regionLanguageFile);
    Object.assign(Locale.languageMap, msoLanguageFile);
    Object.assign(Locale.languageMap, platformLanguageFile);
  };

  /**
   * Reads a display string for `key`, preferring the live string-override
   * value fetched via `DeviceConfigurationService.fetchMsoDefaultStringOverrides`
   * (pre-signin) / `fetchStringOverrides` (post-signin) — same live-over-fallback
   * layering as `Branding.getBrandingUiInfoForKey` — and falling back to the
   * locally bundled cascading locale file (loaded by `setLocale`) when no live
   * override exists for `key`.
   */
  static readonly getString = (key: string): string => {
    try {
      const service: DeviceConfigurationService =
        new DeviceConfigurationService({} as TServicesParams);
      const overrideValue: string | undefined =
        service.getStringOverrideForKey(key);
      if (
        overrideValue !== undefined &&
        overrideValue !== null &&
        overrideValue !== ""
      ) {
        return overrideValue;
      }
    } catch (err: unknown) {
      const error = err as Error;
      Log.e(Locale.TAG, error.message);
    }

    return Locale.languageMap[key] || "";
  };
}
